/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import style from './Form.module.css';
import { FormInput, FormSelect } from '../components/Basic/FormInput';
import Button from '../components/Basic/button';
import { notification, Spin, AutoComplete, Checkbox } from 'antd';
import { FaArrowLeft } from "react-icons/fa";
import { connect } from 'react-redux';
import * as Actions from "../store/slices/user/user"
import * as roleActions from '../store/slices/role/roles'
import * as institutionActions from '../store/slices/institution/institution'
import { hasPermission } from '../utils/premission';
import { PERMISSIONS } from '../utils/constant/permissions';
import { usePasswordPolicy } from '../utils/hooks/usePasswordPolicy';
import { getDecodedToken } from '../utils/auths';

interface Api {
    User_Red: any;
    Role_Red: any;
    Institution_Red: any;
    getAllRole: (page: number, size: number) => unknown;
    getAllInstitutions: () => unknown;
    createUser: (payload: any) => unknown;
    getById: (id: number) => unknown;
    UpdateUser: (id: number, payload: any) => unknown;
}

interface Form {
    UserName: string;
    Role: string;
    email: string;
    Password?: string | undefined;
    institutionId?: string;
}

interface PersonalProps {
    Back: () => void;
    userId?: number;
}

interface Role {
    roleId: number;
    name: string;
    description: string;
    permissionIds: number[];
}

type both = Api & PersonalProps;

const PersonalInfoForm: React.FC<both> = ({
    Back,
    userId,
    User_Red,
    getAllRole,
    Role_Red,
    Institution_Red,
    getAllInstitutions,
    createUser,
    getById,
    UpdateUser
}) => {
    const isEdit = !!userId;
    const [api, contextHolder] = notification.useNotification();
    const [spinning, setSpinning] = useState(false);
    const [currentPage] = useState(0);
    const [pageSize] = useState(20);
    const [adOptions, setAdOptions] = useState<{ value: string; label: string; data: any }[]>([]);
    const [allAdUsers, setAllAdUsers] = useState<string[]>([]);
    const [adSearchText, setAdSearchText] = useState('');
    const [isAdUser, setIsAdUser] = useState(false);

    const { buildSchema, hints } = usePasswordPolicy();
    const decoded = getDecodedToken();
    const tokenInstitutionId = decoded?.institutionId?.toString() ?? '';
    const isInstitutionUser = !!decoded?.institutionId;

    const validationSchema = React.useMemo(() => yup.object().shape({
        UserName: yup.string().required('Field required'),
        Role: yup.string().required('Field required'),
        email: yup.string().email('Invalid email format').required('Field required'),
        Password: isEdit || isAdUser ? yup.string() : buildSchema(),
    }), [isEdit, isAdUser, buildSchema]);

    useEffect(() => {
        getAllRole(currentPage, pageSize);
        getAllInstitutions();
    }, [getAllRole, getAllInstitutions, currentPage, pageSize]);

    const roles: Role[] = Role_Red?.data[0]?.res?.data?.content || [];
    const institutions: any[] = Institution_Red?.data || [];

    const {
        control,
        formState: { errors },
        handleSubmit,
        reset,
        setValue,
        trigger,
    } = useForm<Form>({
        defaultValues: { UserName: '', Role: '', Password: '', email: '', institutionId: tokenInstitutionId },
        mode: 'onChange',
        resolver: (values, context, options) => yupResolver(validationSchema)(values, context, options),
    });

    useEffect(() => {
        const fetchUserData = async () => {
            if (userId) {
                setSpinning(true);
                try {
                    const response: any = await getById(userId);
                    if (response.success) {
                        const data = response.data.data;
                        reset({
                            UserName: data.userName || '',
                            Role: data.roleIds?.[0]?.toString() || '',
                            email: data.email || '',
                            Password: '',
                            institutionId: data.institutionId?.toString() || '',
                        });
                    } else {
                        api.error({ message: 'Error', description: 'Failed to fetch user data.' });
                    }
                } catch {
                    api.error({ message: 'Error', description: 'Failed to fetch user data.' });
                } finally {
                    setSpinning(false);
                }
            } else {
                // create mode — clear form
                reset({ UserName: '', Role: '', Password: '', email: '', institutionId: tokenInstitutionId });
                setIsAdUser(false);
                setAdSearchText('');
                setAdOptions([]);
            }
        };
        fetchUserData();
    }, [userId, getById, reset]);

    useEffect(() => {
        const fetchAdUsers = async () => {
            try {
                const token = localStorage.getItem('loginToken');
                const API_URL = process.env.REACT_APP_API_URL;
                const res = await fetch(`${API_URL}/api/users/get-ad-users`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                const users: string[] = Array.isArray(data) ? data : (data.data || []);
                setAllAdUsers(users);
            } catch {
                api.error({ message: 'AD Fetch Failed', description: 'Could not load AD users.', duration: 5 });
            }
        };
        if (!isEdit) fetchAdUsers();
    }, [isEdit]);

    const handleAdCheckbox = (checked: boolean) => {
        setIsAdUser(checked);
        setAdSearchText('');
        setAdOptions([]);
        reset({ UserName: '', Role: '', email: '', Password: '', institutionId: tokenInstitutionId });
        trigger('Password');
    };

    const onAdSearch = (query: string) => {
        setAdSearchText(query);
        if (!query) { setAdOptions([]); return; }
        const filtered = allAdUsers.filter(u => u.toLowerCase().includes(query.toLowerCase()));
        setAdOptions(filtered.map((email: string) => ({
            value: email,
            label: email,
            data: { email, userName: email },
        })));
    };

    const onAdSelect = (_val: string, selectedOption: any) => {
        const u = selectedOption.data;
        setValue('UserName', u.email, { shouldValidate: true });
        setValue('email', u.email, { shouldValidate: true });
        setAdSearchText(u.email);
        trigger('Password');
    };

    const onSubmit: SubmitHandler<Form> = async (data) => {
        setSpinning(true);
        try {
            let response: any;
            const resolvedInstitutionId = tokenInstitutionId
                ? Number(tokenInstitutionId)
                : data.institutionId ? Number(data.institutionId) : undefined;

            if (isEdit) {
                const payload: any = {
                    userName: data.UserName.trim(),
                    email: data.email,
                    roleIds: [Number(data.Role)],
                    adUser: isAdUser,
                    institutionId: resolvedInstitutionId,
                };
                if (data.Password) payload.password = data.Password;
                response = await UpdateUser(userId, payload);
            } else {
                const createPayload: any = {
                    userName: data.UserName.trim(),
                    email: data.email,
                    roleIds: [Number(data.Role)],
                    adUser: isAdUser,
                    institutionId: resolvedInstitutionId,
                };
                if (!isAdUser && data.Password) createPayload.password = data.Password;
                response = await createUser(createPayload);
            }

            if (response?.success) {
                api.success({
                    message: 'Success',
                    description: isEdit ? `User ${data.UserName} updated.` : `User ${data.UserName} created.`,
                    duration: 2.5,
                });
                setTimeout(() => Back(), 1800);
            } else {
                api.error({
                    message: 'Error',
                    description: response?.message || response?.exceptionMsg || (isEdit ? 'Could not update user.' : 'Could not create user.'),
                });
            }
        } catch (err: any) {
            api.error({
                message: 'Error',
                description: err.message || (isEdit ? 'Could not update user.' : 'Could not create user.'),
            });
        } finally {
            setSpinning(false);
        }
    };

    return (
        <>
            {contextHolder}
            <Spin spinning={spinning} fullscreen />
            <div className={`container-fluid ${style.Container}`}>
                <FaArrowLeft size={24} onClick={Back} className={style.backArrow} />
                <div className={`row ${style.row}`}>
                    <div className={`col-lg-12 col-md-6 col-sm-6 ${style.SignInContainer}`}>

                        {/* AD User checkbox — only on create */}
                        {!isEdit && (
                            <div className={style.userTypeRow}>
                                <Checkbox
                                    checked={isAdUser}
                                    onChange={(e) => handleAdCheckbox(e.target.checked)}
                                >
                                    LDAP User
                                </Checkbox>
                                {isAdUser && (
                                    <span className={style.userTypeBadgeAd}>
                                      Active Directory User — no password required
                                    </span>
                                )}
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)}>

                            {/* AD Search — only when AD checkbox is checked */}
                            {!isEdit && isAdUser && (
                                <div className={style.formSetting}>
                                    <div className={style.field} style={{ width: '25vw' }}>
                                        <label className={style.Label} style={{ marginTop: 10 }}>Search LDAP User</label>
                                        <AutoComplete
                                            value={adSearchText}
                                            options={adOptions}
                                            className={style.User_Input}
                                            onSearch={onAdSearch}
                                            onSelect={onAdSelect}
                                            placeholder="Type name or email to search AD..."
                                            style={{ marginTop: 6 }}
                                            notFoundContent={adSearchText ? 'No AD users found' : null}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className={style.formSetting}>
                                <div className={style.field} style={{ width: '25vw' }}>
                                    <FormInput
                                        Label="User Name"
                                        placeholder="Enter User Name"
                                        classInput={style.Input}
                                        className={style.inputContainer}
                                        labelClass={style.Label}
                                        classError={style.Error}
                                        name="UserName"
                                        type="text"
                                        errors={errors}
                                        control={control}
                                        disabled={!isEdit && isAdUser}
                                    />
                                </div>
                                <div className={style.field} style={{ width: '25vw' }}>
                                    <FormSelect
                                        control={control}
                                        name="Role"
                                        options={roles.map((r) => ({
                                            label: r.name,
                                            value: r.roleId.toString()
                                        }))}
                                        placeholder={roles.length === 0 ? 'Loading roles...' : 'Select Role'}
                                        disabled={roles.length === 0}
                                        errors={errors}
                                        className="form-select-container"
                                        classError={style.Error}
                                        showLabel={true}
                                        Label="Role"
                                        labelClass={style.Label}
                                    />
                                </div>
                            </div>

                            <div className={style.formSetting}>
                                <div className={style.field} style={{ width: '25vw' }}>
                                    <FormInput
                                        Label="Email"
                                        placeholder="Enter Email"
                                        classInput={style.Input}
                                        className={style.inputContainer}
                                        labelClass={style.Label}
                                        classError={style.Error}
                                        name="email"
                                        type="text"
                                        errors={errors}
                                        control={control}
                                        disabled={!isEdit && isAdUser}
                                    />
                                </div>
                                <div className={style.field} style={{ width: '25vw' }}>
                                    <FormSelect
                                        control={control}
                                        name="institutionId"
                                        options={institutions.map((inst: any) => ({
                                            label: inst.institutionName,
                                            value: inst.institutionId?.toString() ?? inst.id?.toString()
                                        }))}
                                        placeholder={institutions.length === 0 ? 'Loading institutions...' : 'Select Institution'}
                                        disabled={institutions.length === 0 || isInstitutionUser}
                                        errors={errors}
                                        className="form-select-container"
                                        classError={style.Error}
                                        showLabel={true}
                                        Label="Institution"
                                        labelClass={style.Label}
                                    />
                                </div>
                            </div>

                            {/* Password — only for local user on create */}
                            {!isEdit && !isAdUser && (
                                <div className={style.formSetting}>
                                    <div className={style.field} style={{ width: '25vw' }}>
                                        <FormInput
                                            Label="Password"
                                            placeholder="Enter password"
                                            classInput={style.Input}
                                            className={style.inputContainer}
                                            labelClass={style.Label}
                                            classError={style.Error}
                                            name="Password"
                                            type="password"
                                            errors={errors}
                                            control={control}
                                        />
                                        <ul className={style.policyHints}>
                                            {hints.map((h, i) => <li key={i}>{h.label}</li>)}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {hasPermission(PERMISSIONS.USER_CREATE || PERMISSIONS.USER_UPDATE) && (
                                <Button
                                    Text={isEdit ? 'Update User' : isAdUser ? 'Create AD User' : 'Create Local User'}
                                    buttonClass={isAdUser ? style.buttonSignInAd : style.buttonSignIn}
                                    Disable={spinning}
                                />
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

function mapStateToProps(state: any) {
    return {
        User_Red: state.User_Red,
        Role_Red: state.Role_Red,
        Institution_Red: state.Institution_Red
    };
}

export default connect(mapStateToProps, { ...Actions, ...roleActions, ...institutionActions })(PersonalInfoForm);
